import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Plus, Edit, Trash2, X, MapPin, Clock, ArrowUp, ArrowDown } from 'lucide-react';
import { notify } from '../../utils/notifications';

const vuoto = { nome: '', ordine: 0, orario_andata: '', orario_ritorno: '', attiva: true };

/**
 * Località servite dal gruppo: paesi di raccolta all'andata e di discesa
 * al ritorno. L'ordine è quello del percorso di rientro.
 */
const LocalitaGruppo = ({ gruppoId }) => {
  const [localita, setLocalita] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [inModifica, setInModifica] = useState(null);
  const [formData, setFormData] = useState(vuoto);

  useEffect(() => {
    carica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gruppoId]);

  const carica = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/trasporti/localita/?gruppo=${gruppoId}`);
      setLocalita(res.data.results || res.data);
    } catch (error) {
      console.error('Errore caricamento località:', error);
    } finally {
      setLoading(false);
    }
  };

  const apri = (loc = null) => {
    if (loc) {
      setInModifica(loc);
      setFormData({
        nome: loc.nome,
        ordine: loc.ordine,
        orario_andata: loc.orario_andata || '',
        orario_ritorno: loc.orario_ritorno || '',
        attiva: loc.attiva,
      });
    } else {
      setInModifica(null);
      setFormData({ ...vuoto, ordine: localita.length + 1 });
    }
    setShowModal(true);
  };

  const salva = async (e) => {
    e.preventDefault();
    const toastId = notify.loading(inModifica ? 'Aggiornamento...' : 'Creazione...');
    const payload = {
      ...formData,
      gruppo: gruppoId,
      orario_andata: formData.orario_andata || null,
      orario_ritorno: formData.orario_ritorno || null,
    };
    try {
      if (inModifica) {
        await api.put(`/trasporti/localita/${inModifica.id}/`, payload);
      } else {
        await api.post('/trasporti/localita/', payload);
      }
      notify.update(toastId, { type: 'success', render: 'Località salvata' });
      await carica();
      setShowModal(false);
    } catch (error) {
      const dati = error.response?.data || {};
      const primo = Object.values(dati).flat().find((v) => typeof v === 'string');
      notify.update(toastId, { type: 'error', render: primo || 'Errore nel salvataggio' });
    }
  };

  const elimina = async (loc) => {
    if (!window.confirm(`Eliminare la località "${loc.nome}"? Gli abbonamenti che la usano resteranno senza località.`)) return;
    const toastId = notify.loading('Eliminazione...');
    try {
      await api.delete(`/trasporti/localita/${loc.id}/`);
      notify.update(toastId, { type: 'success', render: 'Località eliminata' });
      await carica();
    } catch (error) {
      notify.update(toastId, { type: 'error', render: 'Errore nell\'eliminazione' });
    }
  };

  const sposta = async (loc, direzione) => {
    const ordinate = [...localita].sort((a, b) => a.ordine - b.ordine);
    const i = ordinate.findIndex((l) => l.id === loc.id);
    const j = i + direzione;
    if (j < 0 || j >= ordinate.length) return;
    try {
      await Promise.all([
        api.patch(`/trasporti/localita/${ordinate[i].id}/`, { ordine: ordinate[j].ordine }),
        api.patch(`/trasporti/localita/${ordinate[j].id}/`, { ordine: ordinate[i].ordine }),
      ]);
      await carica();
    } catch (error) {
      notify.error('Errore nel riordino');
    }
  };

  if (loading) return <div className="animate-pulse text-sm text-gray-500">Caricamento località...</div>;

  const ordinate = [...localita].sort((a, b) => a.ordine - b.ordine);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-1">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#1b94ce]" />
          Località ({localita.length})
        </h2>
        <button
          onClick={() => apri()}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition"
        >
          <Plus className="w-4 h-4" />
          Nuova Località
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Paesi di raccolta all'andata e di discesa al ritorno. L'ordine è quello del percorso di rientro.
      </p>

      {ordinate.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MapPin className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>Nessuna località configurata</p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {ordinate.map((loc, i) => (
            <div key={loc.id} className={`px-4 py-3 flex items-center justify-between gap-3 ${!loc.attiva ? 'bg-gray-50' : ''}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-7 h-7 rounded-full bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center shrink-0">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {loc.nome}
                    {!loc.attiva && <span className="ml-2 text-xs font-normal text-gray-500">(non attiva)</span>}
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-3">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> andata {loc.orario_andata?.slice(0, 5) || '—'}
                    </span>
                    <span className="flex items-center gap-1">
                      rientro {loc.orario_ritorno?.slice(0, 5) || '—'}
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => sposta(loc, -1)} disabled={i === 0} title="Sposta su"
                  className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition disabled:opacity-30">
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button onClick={() => sposta(loc, 1)} disabled={i === ordinate.length - 1} title="Sposta giù"
                  className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition disabled:opacity-30">
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button onClick={() => apri(loc)} title="Modifica"
                  className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition">
                  <Edit className="w-4 h-4" />
                </button>
                <button onClick={() => elimina(loc)} title="Elimina"
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">
                {inModifica ? 'Modifica Località' : 'Nuova Località'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={salva} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Es. Vitulazio"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Orario raccolta (andata)</label>
                  <input
                    type="time"
                    value={formData.orario_andata}
                    onChange={(e) => setFormData({ ...formData, orario_andata: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Orario rientro</label>
                  <input
                    type="time"
                    value={formData.orario_ritorno}
                    onChange={(e) => setFormData({ ...formData, orario_ritorno: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    È l'orario mostrato a chi scende qui
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="loc-attiva"
                  checked={formData.attiva}
                  onChange={(e) => setFormData({ ...formData, attiva: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="loc-attiva" className="text-sm text-gray-700">
                  Attiva — selezionabile sui nuovi abbonamenti
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold text-sm">
                  Annulla
                </button>
                <button type="submit"
                  className="px-5 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold text-sm">
                  Salva
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalitaGruppo;
